import React, { useEffect, useState } from 'react';
import { TrendingUp, Users, DollarSign } from 'lucide-react';
import { partnershipsService } from '../../services/partnerships.service';

interface PipelineStats {
    total: number;
    totalValue: number;
    byStage: Record<string, number>;
    conversionRate: number;
    avgDealValue: number;
}

const PipelineAnalyticsCard: React.FC = () => {
    const [stats, setStats] = useState<PipelineStats | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const data = await partnershipsService.getPipelineStats();
                setStats(data);
            } catch (error) {
                console.error('Failed to fetch pipeline stats:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (isLoading) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 h-64 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (!stats) return null;

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const stages = [
        { key: 'Lead', label: 'Leads', color: 'bg-gray-200 dark:bg-gray-700' },
        { key: 'Pitch Sent', label: 'Pitched', color: 'bg-blue-200 dark:bg-blue-900/40' },
        { key: 'Contract Sent', label: 'Negotiating', color: 'bg-yellow-200 dark:bg-yellow-900/40' },
        { key: 'Payment Received', label: 'Closed', color: 'bg-green-200 dark:bg-green-900/40' },
    ];

    const maxCount = Math.max(...Object.values(stats.byStage));

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Partnership Pipeline</h3>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <TrendingUp className="w-4 h-4" />
                    <span>{stats.conversionRate}% Conv.</span>
                </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                        <DollarSign className="w-4 h-4" />
                        <span className="text-xs font-medium uppercase">Pipeline Value</span>
                    </div>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {formatCurrency(stats.totalValue)}
                    </p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                        <Users className="w-4 h-4" />
                        <span className="text-xs font-medium uppercase">Total Deals</span>
                    </div>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {stats.total}
                    </p>
                </div>
            </div>

            {/* Funnel Visualization */}
            <div className="space-y-3">
                {stages.map((stage) => {
                    const count = stats.byStage[stage.key] || 0;
                    const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;

                    return (
                        <div key={stage.key} className="relative">
                            <div className="flex items-center justify-between text-sm mb-1">
                                <span className="text-gray-600 dark:text-gray-300">{stage.label}</span>
                                <span className="font-medium text-gray-900 dark:text-white">{count}</span>
                            </div>
                            <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                                <div
                                    className={`h-full rounded-full ${stage.color.replace('bg-', 'bg-opacity-100 bg-')}`}
                                    style={{ width: `${Math.max(percentage, 5)}%` }} // Min width for visibility
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Avg. Deal Size</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(stats.avgDealValue)}</span>
            </div>
        </div>
    );
};

export default PipelineAnalyticsCard;
